import dayjs from "dayjs";
import _ from "lodash";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { API } from "../../utils/AxiosInstance";
import { CACHE_30_MINS } from "../../utils/cache-utils";
import { apiDate } from "../../utils/DateUtils";

const CMS_IN_FOOT = 30.48;
const CMS_IN_INCH = 2.54;

export type Gender = "male" | "female" | "not sure" | "prefer not to answer";

export interface SubscriptionInfo {
    banner_type:
        | "none"
        | "program_ending_soon"
        | "payment_overdue"
        | "india_payment_required";
    days_until_payment_due: number;
    payment_due_date: string;
    is_subscribed: boolean;
    can_subscribe_now: boolean;
}

export interface AppAccess {
    access: boolean;
    display_header_msg?: string;
    display_message?: string;
}

export interface Profile {
    id?: string;
    firstName?: string;
    lastName?: string;
    preferredName?: string;
    gender?: Gender;
    dateOfBirth: Date | null;
    email: string;
    avatarUrl?: string;
    phone: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zipCode: string;
    timezone: string;
    height?: {
        feet: number;
        inches: number;
        cm: number;
        heightUnit: string;
    };
    waist: number;
    hips: number;
    diabetes?: string;
    activityLevel?: string;
    firstTimeUser: boolean;
    addressConfirmed: boolean;
    isParticipant: boolean;
    isEnrolled: boolean;
    current_gender: string;
    cellPhoneVerified?: boolean;
    enableCohortSelection: boolean;
    dppStartDate: string;
    joinDate: string;
    course?: string;
    cohort?: string;
    hypertension?: string;
    heightUnit?: string;
    weightUnit?: string;
    completedOnboardingGuide: boolean;
    appAccess?: AppAccess;
    subscriptionInfo?: SubscriptionInfo;
    countryCode?: string; // Added country code field
}

interface RemoteProfile {
    id?: string;
    first_name?: string;
    last_name?: string;
    preferred_name?: string;
    gender?: Gender;
    date_of_birth: string;
    email: string;
    avatar_url?: string;
    cell_phone?: string;
    mailaddress1?: string;
    mailaddress2?: string;
    city: string;
    state: string;
    zip_code?: string;
    timezone?: string;
    height?: number;
    height_unit?: string;
    weight_unit?: string;
    waist?: number;
    hips?: number;
    diabetes?: string;
    activity_level?: string;
    hypertension?: string;
    first_time_user: boolean;
    address_confirmed: boolean;
    is_participant: boolean;
    is_enrolled: boolean;
    current_gender: string;
    cell_phone_verified?: boolean;
    enable_cohort_selection: boolean;
    dpp_date: string;
    join_date: string;
    course?: string;
    cohort?: string;
    completed_onboarding_guide: boolean;
    country_code?: string; // Added country code field
    app_access?: AppAccess;
    subscription_info?: SubscriptionInfo;
}

export interface DisenrollUser {
    not_enrolled_reason: string;
    not_enrolled_code: string;
}

export const useProfile = (disabled = false) =>
    useQuery<Profile, Error>(
        "profile",
        () =>
            API.get<RemoteProfile>("/profile")
                .then(({ data }) => data)
                .then(
                    ({
                        id,
                        first_name,
                        last_name,
                        preferred_name,
                        gender,
                        date_of_birth,
                        email,
                        avatar_url,
                        cell_phone,
                        mailaddress1,
                        mailaddress2,
                        city,
                        state,
                        zip_code,
                        timezone,
                        height,
                        height_unit,
                        waist,
                        hips,
                        diabetes,
                        activity_level,
                        hypertension,
                        first_time_user,
                        address_confirmed,
                        completed_onboarding_guide,
                        is_participant,
                        is_enrolled,
                        cell_phone_verified,
                        current_gender,
                        enable_cohort_selection,
                        course,
                        cohort,
                        dpp_date,
                        join_date,
                        app_access,
                        subscription_info,
                        country_code,
                        weight_unit,
                    }) => {
                        const userPreferredUnit = height_unit ?? "cm";
                        const converted = {
                            id,
                            firstName: first_name,
                            lastName: last_name,
                            preferredName: preferred_name,
                            gender,
                            dateOfBirth:
                                date_of_birth &&
                                date_of_birth.trim().length !== 0
                                    ? dayjs(date_of_birth).toDate()
                                    : null,
                            email,
                            avatarUrl: avatar_url,
                            phone: cell_phone,
                            addressLine1: mailaddress1,
                            addressLine2: mailaddress2,
                            city,
                            state,
                            zipCode: zip_code,
                            timezone,
                            height: (() => {
                                const remoteHeightInCm = height;
                                if (typeof remoteHeightInCm === "number") {
                                    if (
                                        userPreferredUnit === "ft" ||
                                        userPreferredUnit === "ft-in"
                                    ) {
                                        const totalInches =
                                            remoteHeightInCm / CMS_IN_INCH;
                                        const feet =
                                            Math.round(totalInches % 12) === 12
                                                ? Math.floor(totalInches / 12) +
                                                  1
                                                : Math.floor(totalInches / 12);
                                        const inches =
                                            Math.round(totalInches % 12) === 12
                                                ? 0
                                                : Math.round(totalInches % 12);
                                        return {
                                            feet,
                                            inches,
                                            cm: remoteHeightInCm,
                                            heightUnit: "ft-in",
                                        };
                                    }
                                    return {
                                        cm: Math.round(remoteHeightInCm),
                                        heightUnit: "cm",
                                    };
                                }
                                return undefined;
                            })(),
                            heightUnit: userPreferredUnit,
                            weightUnit: weight_unit ?? "lbs",
                            waist: waist
                                ? _.round(waist / CMS_IN_INCH, 0)
                                : waist,
                            hips: hips ? _.round(hips / CMS_IN_INCH, 0) : hips,
                            diabetes,
                            activityLevel: activity_level,
                            firstTimeUser: first_time_user,
                            addressConfirmed: address_confirmed,
                            isParticipant: is_participant,
                            current_gender,
                            isEnrolled: is_enrolled,
                            cellPhoneVerified: cell_phone_verified,
                            enableCohortSelection:
                                enable_cohort_selection || false,
                            dppStartDate: dpp_date,
                            joinDate: join_date,
                            course,
                            cohort,
                            hypertension,
                            completedOnboardingGuide:
                                completed_onboarding_guide,
                            appAccess: app_access,
                            subscriptionInfo: subscription_info,
                            countryCode: country_code,
                        } as Profile;
                        return converted;
                    }
                ),
        {
            staleTime: CACHE_30_MINS,
            enabled: !disabled,
        }
    );

export const useUpdateMutation = () => {
    const queryCient = useQueryClient();

    return useMutation(
        ({
            firstName,
            lastName,
            preferredName,
            gender,
            dateOfBirth,
            phone,
            addressLine1,
            addressLine2,
            city,
            state,
            zipCode,
            addressConfirmed,
            timezone,
            height,
            waist,
            hips,
            diabetes,
            activityLevel,
            firstTimeUser,
            current_gender,
            hypertension,
        }: Partial<Profile>) => {
            let heightInCm: number | undefined;
            let heightUnit: string | undefined;

            if (height) {
                heightUnit = height.heightUnit;

                if (height.heightUnit === "cm") {
                    heightInCm = height.cm;
                } else if (
                    height.heightUnit === "ft" ||
                    height.heightUnit === "ft-in"
                ) {
                    heightInCm =
                        height.feet * CMS_IN_FOOT +
                        (height.inches || 0) * CMS_IN_INCH;
                }
            }

            const payload = {
                first_name: firstName,
                last_name: lastName,
                preferred_name: preferredName,
                gender,
                date_of_birth: dateOfBirth ? apiDate(dateOfBirth) : null,
                cell_phone: phone,
                mailaddress1: addressLine1,
                mailaddress2: addressLine2,
                city,
                state,
                zip_code: zipCode,
                address_confirmed: addressConfirmed,
                timezone,
                height: heightInCm,
                height_unit: heightUnit,
                waist: waist ? waist * CMS_IN_INCH : waist,
                hips: hips ? hips * CMS_IN_INCH : hips,
                diabetes,
                activity_level: activityLevel,
                first_time_user: firstTimeUser,
                current_gender,
                hypertension,
            };

            return API.put<Partial<RemoteProfile>>("/profile", payload).then(
                () => queryCient.invalidateQueries("profile")
            );
        }
    );
};

export const useDisenrollMutation = () =>
    useMutation(({ not_enrolled_code, not_enrolled_reason }: DisenrollUser) =>
        API.put<DisenrollUser>("/profile/disenroll_user", {
            not_enrolled_code,
            not_enrolled_reason,
        })
            .then(() => Promise.resolve("success"))
            .catch((_error) =>
                Promise.reject(new Error("Error uploading profile picture"))
            )
    );

const MAX_PICTURE_SIZE = 5 * 1024 * 1024;

const validateFileSize = (file: File) => {
    if (file.size > MAX_PICTURE_SIZE) {
        throw new Error(
            `${file.type}  File is too large, please pick a smaller file\n`
        );
    }

    if (!/\.(gif|jpg|jpeg|tiff|png)$/i.test(file.name)) {
        throw new Error("Please select a picture to upload");
    }
};

export const useUploadMutation = () => {
    const queryCient = useQueryClient();

    return useMutation((file: File) => {
        if (!file) {
            throw new Error("No file selected for uploading");
        }

        validateFileSize(file);

        const formData = new FormData();
        formData.append("file", file);

        return API.post<Profile>(`/profiles/avatar`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        })
            .then(() => queryCient.invalidateQueries("profile"))
            .catch((_error) =>
                Promise.reject(new Error("Error uploading profile picture"))
            );
    });
};
